/**
 * RunDisplayWithColor - Shared component for displaying run names with color pills
 *
 * This component provides consistent styling for run displays across the MediaBrowser panel,
 * matching the visual style of the RunSelector but without the visibility controls.
 *
 * Features:
 * - Color pill indicator (16x16px circle to match RunSelector's ColorPicker)
 * - Run name display with truncation support
 * - Optional link support
 * - Configurable text styling
 */

import React from 'react';
import {twMerge} from 'tailwind-merge';

import {type RunSignature} from '../../../types/run';
import ColorPicker from '../../ColorPicker';
import {SingleRunLink} from '../../MultiRunWorkspace/SingleRunMode';

export type RunDisplayWithColorProps = {
  /** Run display name to show */
  displayName: string;
  /** Color for the pill indicator */
  color?: string;
  /** Optional className for the container */
  className?: string;
  /** Optional className for the text */
  textClassName?: string;
  /** Optional run signature for linking */
  runSignature?: RunSignature;
  /** If true, don't create a link even if runSignature is provided */
  disableLink?: boolean;
  /** Whether to show the colored pill, or colorize the name */
  showColorPill?: boolean;
  /** If true, allow text to wrap with word-breaking instead of truncating */
  allowWordBreak?: boolean;
  /** Optional test identifier */
  'data-test'?: string;
};

/**
 * Displays a run name with a color pill indicator.
 * Matches the styling of RunSelector's color picker and label.
 *
 * @param props - RunDisplayWithColorProps
 * @returns React element
 */
export const RunDisplayWithColor: React.FC<RunDisplayWithColorProps> = ({
  displayName,
  color,
  className,
  textClassName,
  runSignature,
  disableLink = false,
  showColorPill = false,
  allowWordBreak = false,
  'data-test': dataTest = 'run-display-with-color',
}) => {
  const textContent =
    runSignature && !disableLink ? (
      <SingleRunLink {...runSignature} linkText={displayName} />
    ) : (
      displayName
    );
  const combinedTextClasses = twMerge(
    'flex-1 min-w-0',
    allowWordBreak ? 'break-words' : 'text__single-line truncate',
    textClassName
  );
  return (
    <div
      className={twMerge(
        'flex flex-row items-start justify-start gap-2 h-full leading-6',
        className
      )}
      data-test={dataTest}>
      {color && showColorPill && (
        <div className="flex flex-shrink-0 items-start justify-start min-h-[24px]">
          <ColorPicker activeColor={color} readOnly={true} />
        </div>
      )}
      <div
        className={combinedTextClasses}
        title={displayName}
        style={color && !showColorPill ? {color} : undefined}>
        {textContent}
      </div>
    </div>
  );
};
